import { List } from "immutable";
import EnhancedContentRecord from "models/view-models/enhanced-content-record";
import { useContext, useState } from "react";
import { ToastManager } from "utilities/toast/toast-manager";
import { CodeReferenceBlockEnhancedContentContext } from "utilities/contexts/notebooks/use-code-reference-block-enhanced-content-context";
import EnhancedContentService from "utilities/services/sections/enhanced-content/enhanced-content-service";
import FileService from "utilities/services/file-service";
import EnhancedContentResourceService from "utilities/services/enhanced-content-resources/enhanced-content-resource-service";
import { CollectionUtils } from "utilities/collection-utils";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { useSharedPageContext } from "utilities/contexts/shared-page/shared-page-context";
import SharedPageEnhancedContentService from "utilities/services/shared-page-sections/enhanced-contents/shared-enhanced-content-service";
import SharedEnhancedContentResourceService from "utilities/services/shared-page-sections/enhanced-contents/shared-enhanced-content-resource-service";

interface UseEnhancedContentOptions {
    code?: string;
    edition?: string;
    externalIds?: string[];
    externalId?: string;
    disabled?: boolean;
}

const BATCH_REQUEST_SIZE = 100;

const useNotebooksEnhancedContent = (options: UseEnhancedContentOptions) => {
    const { code, edition, externalIds, disabled, externalId } = options;

    // Change as part of LINK-1874
    const { currentIdentity, isLoggedIn } = useCurrentIdentity();
    const isUnauthorizedUserForEnhancedContent =
        !isLoggedIn ||
        (isLoggedIn && !currentIdentity?.isConfiguredWithActiveSubscription());

    const [enhancedContent, setEnhancedContent] = useContext(
        CodeReferenceBlockEnhancedContentContext
    );

    const [loadingEnhancedContent, setLoadingEnhancedContent] = useState(false);
    const { list: listEnhancedContent } = EnhancedContentService.useBatchList();
    const { list: sharedPageListEnhancedContent } =
        SharedPageEnhancedContentService.useBatchList();

    const { list: listResources } =
        EnhancedContentResourceService.useBatchList();
    const { list: sharedListResources } =
        SharedEnhancedContentResourceService.useBatchList();
    const { list: listFiles } = FileService.useBatchedList();

    const { pageGuid, recipientGuid } = useSharedPageContext();

    const isSharedUnauthenticatedPage =
        pageGuid && isUnauthorizedUserForEnhancedContent;

    // Given a chaper, gets NFPA Insights for that chapter and returns it
    const getEnhancedContentFromSectionIds = async () => {
        try {
            if (
                code === undefined ||
                edition === undefined ||
                externalIds === undefined
            ) {
                return [];
            }
            setLoadingEnhancedContent(true);
            const enhancedContentResponse = isSharedUnauthenticatedPage
                ? await sharedPageListEnhancedContent(
                      { pageGuid },
                      { code, edition, externalIds, recipient: recipientGuid },
                      "externalIds",
                      100
                  )
                : await listEnhancedContent(
                      { code, edition, externalIds },
                      "externalIds",
                      100
                  );
            const enhancedContentIds =
                enhancedContentResponse.resultObjects.map((e) => e.id!);

            const resourcesResponse = isSharedUnauthenticatedPage
                ? await sharedListResources(
                      { pageGuid },
                      { enhancedContentIds, recipient: recipientGuid },
                      "enhancedContentIds",
                      BATCH_REQUEST_SIZE
                  )
                : await listResources(
                      { enhancedContentIds },
                      "enhancedContentIds",
                      BATCH_REQUEST_SIZE
                  );

            // Query for files related to NFPA Insights resources
            const fileIds = CollectionUtils.uniqueValuesByProperty(
                resourcesResponse.resultObjects,
                "fileId"
            );
            const { resultObjects: files } = await listFiles(
                { ids: fileIds },
                "ids",
                BATCH_REQUEST_SIZE
            );

            // Merge in files to resources
            const resourcesWithFiles = resourcesResponse.resultObjects.map(
                (resource) => {
                    return resource.withFile(
                        files.filter((file) => file.id === resource.fileId)
                    );
                }
            );

            // Merge in resources to NFPA Insights
            return enhancedContentResponse.resultObjects.map((content) => {
                return content.with({
                    resources: resourcesWithFiles.filter(
                        (resource) => resource.enhancedContentId === content.id
                    ),
                });
            });
        } catch (error) {
            ToastManager.error(`Error getting NFPA Insights: ${error}`);
            return [];
        } finally {
            setLoadingEnhancedContent(false);
        }
    };
    // Checks to see if NFPA Insights is needed on chapter change
    const updateEnhancedContentOnBlockChange = async () => {
        if (disabled) return;

        // If NFPA Insights is not available for that chapter,
        // and if we're not already calling for NFPA Insights, make the call
        if (
            code !== undefined &&
            edition !== undefined &&
            externalIds !== undefined &&
            !loadingEnhancedContent
        ) {
            const results = await getEnhancedContentFromSectionIds();
            setEnhancedContent(results);
        }
    };

    const updateEnhancedContentOnEdit = (
        updatedEnhancedContent: EnhancedContentRecord
    ) => {
        const newEnhancedContentAtom = enhancedContent.map((element) => {
            return element.id === updatedEnhancedContent.id
                ? updatedEnhancedContent
                : element;
        });

        setEnhancedContent(newEnhancedContentAtom);
    };

    const updateEnhancedContentOnDelete = (idToDelete: number | undefined) => {
        const newEnhancedContentAtom = enhancedContent.filter(
            (element) => element.id !== idToDelete
        );
        setEnhancedContent(newEnhancedContentAtom);
    };

    const updateEnhancedContentOnAdd = (
        newEnhancedContent: EnhancedContentRecord
    ) => {
        setEnhancedContent((prev) => [...prev, newEnhancedContent]);
    };

    const enhancedContentForCurrentCaapsItem =
        enhancedContent?.find((element) => element.externalId === externalId) ??
        null;

    // NFPA Insights resources are typed as Lists, not arrays. Not sure why, but adding this here is easier than changing it everywhere
    const enhancedContentResourcesForCurrentCaapsItem = List(
        enhancedContentForCurrentCaapsItem?.resources ?? []
    );

    return {
        enhancedContent,
        enhancedContentForCurrentCaapsItem,
        enhancedContentResourcesForCurrentCaapsItem,
        updateEnhancedContentOnBlockChange,
        updateEnhancedContentOnAdd,
        updateEnhancedContentOnEdit,
        updateEnhancedContentOnDelete,
    };
};

export default useNotebooksEnhancedContent;
